import { afterRenderEffect, Component, DestroyRef, effect, inject, OnDestroy, Renderer2, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { ClientProviderEvents, EventHandler, FeatureFlagService, OpenFeature, ProviderEvents, ServerProviderEvents } from '@openfeature/angular-sdk';
import { NgpToastManager } from 'ng-primitives/toast';

import { OnboardingWizardTrigger } from '@customer-portal/domains/onboarding/api';
import { NS_TOAST_INTENTS, NsToast } from '@customer-portal/indicators/toast';
import { ActiveMemberState, CustomerListStore } from '@customer-portal/shared/account/data-access';
import { AnalyticsTracker } from '@customer-portal/shared/analytics';
import { TagManagerService, UserConsentState } from '@customer-portal/utils';

@Component({
  imports: [RouterModule, OnboardingWizardTrigger],
  selector: 'app-root',
  templateUrl: './app-root.html',
  styleUrl: './app-root.scss',
})
export class AppRoot implements OnDestroy {
  private readonly activeMemberService = inject(ActiveMemberState);
  private readonly tagManagerService = inject(TagManagerService);
  private readonly analyticsTracker = inject(AnalyticsTracker);
  private readonly userConsentState = inject(UserConsentState);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);
  private readonly customerListStore = inject(CustomerListStore);
  private readonly toastManager = inject(NgpToastManager);

  /**
   * Feature flags to be tracked for adding/removing body classes.
   * @example
   * For a feature flag named 'new-dashboard', the body will have classes 'ff-new-dashboard--on' or 'ff-new-dashboard--off' based on its state.
   */
  private readonly TRACKED_FEATURE_FLAGS = [];

  private readonly onUserAccountChange = effect(() => {
    const userId = this.activeMemberService.userId();
    const userRole = this.activeMemberService.member()?.roleName;
    const activeCustomerIdHash = this.activeMemberService.activeCustomerIdHash();
    const activeCustomerReferenceHash = this.activeMemberService.activeCustomerReferenceHash();

    if (!userId || !userRole || !activeCustomerIdHash || !activeCustomerReferenceHash) {
      return;
    }

    // The customer id is the same as organisation id in Keycloak
    // The customer reference is a name coming from Keycloak that translates to customer number in the rest of NS
    this.tagManagerService.setUser(userId, userRole, activeCustomerIdHash, activeCustomerReferenceHash);
    void this.analyticsTracker.trackLogin(userId);
  });

  private readonly onUserIdChange = effect(() => {
    const userId = this.activeMemberService.userId();

    untracked(() => {
      this.customerListStore.setUserId(userId);
    });
  });

  private readonly configurationChanged = signal(0); // increase delay for subsequent requests to ensure server has refreshed configuration

  constructor() {
    this.initOpenFeatures();
    this.userConsentState.initUserConsentEvents();
    this.userConsentState.initUserConsentListeners();
    this.initConsentMessageHandling();
    this.userConsentState.exposeTestConsentSetter();
    this.trackFeatureFlags();

    // Remove Cookiebot temporary hidden style after render to prevent
    // displaying the cookie banner before the user is logged in
    afterRenderEffect(() => {
      const cookiebotLoader = (window as unknown as { cookiebotLoader?: { removeTemporaryHiddenStyle: () => void } }).cookiebotLoader;

      if (cookiebotLoader) {
        cookiebotLoader.removeTemporaryHiddenStyle();
      }
    });
  }

  private readonly configurationChangedHandler: EventHandler<ServerProviderEvents | ClientProviderEvents> = () => {
    this.configurationChanged.set(500);
  };

  private initOpenFeatures(): void {
    OpenFeature.addHandler(ProviderEvents.ConfigurationChanged, this.configurationChangedHandler);
  }

  /**
   * Subscribes to consent message events from UserConsentState and displays toasts.
   */
  private initConsentMessageHandling(): void {
    this.userConsentState.consentMessage$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      const intent = event.type === 'accept' ? NS_TOAST_INTENTS.CONFIRM : event.type === 'decline' ? NS_TOAST_INTENTS.CONFIRM : NS_TOAST_INTENTS.INFORM;

      this.toastManager.show(NsToast, {
        context: {
          intent,
          header: event.message,
        },
      });
    });
  }

  public ngOnDestroy(): void {
    OpenFeature.removeHandler(ProviderEvents.ConfigurationChanged, this.configurationChangedHandler);
  }

  /**
   * Tracks the specified feature flags and updates the body classes accordingly.
   */
  private trackFeatureFlags() {
    this.TRACKED_FEATURE_FLAGS.forEach((flag) => {
      this.featureFlagService
        .getBooleanDetails(flag, false)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((details) => {
          const onClass = `${flag}--on`;
          const offClass = `${flag}--off`;
          const flagIsActive = details.value;
          this.renderer.removeClass(document.body, flagIsActive ? offClass : onClass);
          this.renderer.addClass(document.body, flagIsActive ? onClass : offClass);
        });
    });
  }
}
